/**
 * Financial Snapshot breakdown for trusts/companies (display).
 * Engine uses structureAttribution.js (same apportionment and net-asset rules).
 */
import {
    getEntityIdFromOwnership,
    isEntityOwnership,
    getApplicantsCombinedApportionmentPercent,
} from '../../../shared/src/calculation/constants/clientStructureTypes.ts';
import type { ClientStructure } from '../../../shared/src/calculation/constants/clientStructureTypes.ts';
import type { FactFind } from '../../../shared/src/calculation/factFindTypes.ts';
import { villageInterestToday } from '../../../shared/src/calculation/support/villageExit.ts';
import {
    isLiquidAssetType,
    isEntityLiability,
    resolveLiabilityEntityId,
} from './structureLiabilitiesUtils.ts';
import type { DraftFactFind } from '../constants/initialFactFind.ts';

const PERSONAL_OWNERSHIP = new Set(['client', 'partner', 'joint']);

interface EntityAssetBucket {
    gross: number;
    liquid: number;
    liabilities: number;
}

function sumAssetValues(factFind: DraftFactFind): {
    individually: number;
    individualLiquid: number;
    byEntityId: Record<string, EntityAssetBucket>;
} {
    let individually = 0;
    let individualLiquid = 0;
    const byEntityId: Record<string, EntityAssetBucket> = {};

    (factFind.structures || []).forEach((s) => {
        byEntityId[s.id ?? ''] = { gross: 0, liquid: 0, liabilities: 0 };
    });

    Object.entries(factFind.assets || {}).forEach(([type, assetList]) => {
        (assetList || []).forEach((asset) => {
            const value = asset.value || 0;
            if (!value) return;
            const { ownership } = asset;
            if (isEntityOwnership(ownership)) {
                const entityId = getEntityIdFromOwnership(ownership);
                if (entityId && entityId !== '__unsure__' && byEntityId[entityId]) {
                    byEntityId[entityId]!.gross += value;
                    if (isLiquidAssetType(type)) {
                        byEntityId[entityId]!.liquid += value;
                    }
                }
            } else if (typeof ownership === 'string' && PERSONAL_OWNERSHIP.has(ownership)) {
                individually += value;
                if (isLiquidAssetType(type)) {
                    individualLiquid += value;
                }
            }
        });
    });

    const home = factFind.home || {};
    const homeValue = home.value || 0;
    if (homeValue > 0) {
        if (home.ownership === 'other_entity' && home.entityId && home.entityId !== '__unsure__' && byEntityId[home.entityId]) {
            byEntityId[home.entityId]!.gross += homeValue;
        } else if (home.ownership !== 'other_entity') {
            individually += homeValue;
        }
    }
    // A retirement village interest is wealth like the home (8 Oct 2026): what the village would pay
    // back today. The same figure the server puts in net worth, so the card's total adds up to it.
    individually += villageInterestToday(factFind as unknown as FactFind);

    Object.values(factFind.liabilities || {}).forEach((list) => {
        (list || []).forEach((item) => {
            const balance = item.balance || 0;
            if (!balance) return;
            const entityId = resolveLiabilityEntityId(item, factFind);
            if (entityId && byEntityId[entityId]) {
                byEntityId[entityId]!.liabilities += balance;
            }
        });
    });

    return { individually, individualLiquid, byEntityId };
}

function sumPersonalLiabilities(factFind: DraftFactFind): number {
    let total = 0;
    Object.values(factFind.liabilities || {}).forEach((list) => {
        (list || []).forEach((item) => {
            if (isEntityLiability(item, factFind)) return;
            total += item.balance || 0;
        });
    });
    return total;
}

export interface StructureSnapshotEntityLine {
    id: string;
    name: string;
    grossTotal: number;
    liabilitiesTotal: number;
    netGross: number;
    applicantsShare: number;
    applicantsLiquidShare: number;
    combinedApportionmentPct: number;
}

export interface StructureSnapshotBreakdown {
    hasStructures: boolean;
    individually: number;
    individualLiquid: number;
    entityLines: StructureSnapshotEntityLine[];
    totalAssets: number;
    totalLiabilities: number;
    personalLiabilities: number;
    combinedNetWorth: number;
    applicantsLiquidNet: number;
}

export function computeStructureSnapshotBreakdown(
    factFind: DraftFactFind,
    isPartnered: boolean = factFind.adviceFor === 'couple',
): StructureSnapshotBreakdown {
    const structures = factFind.structures || [];
    const hasStructures = structures.length > 0 || factFind.structuresGateAnswer === true;

    const { individually, individualLiquid, byEntityId } = sumAssetValues(factFind);
    const personalLiabilities = sumPersonalLiabilities(factFind);

    const entityLines: StructureSnapshotEntityLine[] = structures.map((s) => {
        const bucket = byEntityId[s.id ?? ''] || { gross: 0, liquid: 0, liabilities: 0 };
        const netGross = Math.max(0, bucket.gross - bucket.liabilities);
        // DraftFactFind's structure entries are structurally compatible with ClientStructure
        // (see types/formDraft.ts); the helper does `Math.min/Math.max` arithmetic that coerces via
        // `|| 0` regardless, so this cast is safe either way.
        const combinedPct = getApplicantsCombinedApportionmentPercent(s as unknown as ClientStructure, isPartnered);
        const applicantsShare = netGross * (combinedPct / 100);
        const netLiquid = Math.max(0, bucket.liquid - bucket.liabilities);
        const applicantsLiquidShare = netLiquid * (combinedPct / 100);
        return {
            id: s.id ?? '',
            name: s.name?.trim() || 'Unnamed arrangement',
            grossTotal: bucket.gross,
            liabilitiesTotal: bucket.liabilities,
            netGross,
            applicantsShare,
            applicantsLiquidShare,
            combinedApportionmentPct: combinedPct,
        };
    });

    const entityApplicantsTotal = entityLines.reduce((sum, line) => sum + line.applicantsShare, 0);
    const entityLiquidTotal = entityLines.reduce((sum, line) => sum + line.applicantsLiquidShare, 0);
    const totalAssets = individually + entityLines.reduce((sum, line) => sum + line.grossTotal, 0);
    const totalLiabilities =
        personalLiabilities + entityLines.reduce((sum, line) => sum + line.liabilitiesTotal, 0);
    const combinedNetWorth = individually + entityApplicantsTotal - personalLiabilities;
    const applicantsLiquidNet = Math.max(0, individualLiquid - personalLiabilities) + entityLiquidTotal;

    return {
        hasStructures,
        individually,
        individualLiquid,
        entityLines,
        totalAssets,
        totalLiabilities,
        personalLiabilities,
        combinedNetWorth,
        applicantsLiquidNet,
    };
}
